'use client';

import { useEffect } from 'react';
import useSWR from 'swr';
import { getTeamChannel } from '@/lib/ws-client';

const fetcher = (url: string) => fetch(url).then((res) => res.json());

export interface InstanceSummary {
  id: number;
  instanceName: string;
  displayName?: string | null;
  integration?: string;
  status?: string;
}

export interface TeamWithInstances {
  id: number;
  name?: string;
  logoUrl?: string | null;
  planId?: string | null;
  evolutionInstances?: any[];
  websiteChatbotSettings?: {
    isInstalled?: boolean;
    installedAt?: string;
    installedDomain?: string;
  } | null;
  hasReceivedMessages?: boolean;
  isChatbotInstalled?: boolean;
  isUnlocked?: boolean;
}

export function useInstanceStatus() {
  const { 
    data: team, 
    isLoading: isTeamLoading, 
    mutate: mutateTeam 
  } = useSWR<TeamWithInstances>('/api/team', fetcher, {
    revalidateOnFocus: true,
    revalidateIfStale: true,
  });

  const { 
    data: instances, 
    isLoading: isInstancesLoading, 
    mutate: mutateInstances 
  } = useSWR<InstanceSummary[]>('/api/instance/list', fetcher, {
    revalidateOnFocus: true,
    revalidateIfStale: true,
  });

  const isLoading = isTeamLoading && isInstancesLoading;

  // Has actual WhatsApp instance
  const instanceCount = Array.isArray(instances)
    ? instances.length
    : Array.isArray(team?.evolutionInstances)
    ? team!.evolutionInstances!.length
    : 0;

  const hasActualInstance = instanceCount > 0;

  // Has website chatbot installed on a website
  const isChatbotInstalled = Boolean(
    team?.isChatbotInstalled || team?.websiteChatbotSettings?.isInstalled
  );

  // Has received messages
  const hasReceivedMessages = Boolean(team?.hasReceivedMessages);

  // Automatic unlock if WhatsApp instance exists, or website chatbot is installed, or any message received!
  const isUnlocked = hasActualInstance || isChatbotInstalled || hasReceivedMessages;

  const refreshInstances = async () => {
    await Promise.all([mutateTeam(), mutateInstances()]);
  };

  // Listen to real-time unlock broadcasts
  useEffect(() => {
    if (!team?.id) return;
    const channel = getTeamChannel(team.id);
    if (!channel) return;

    const handleUnlock = () => {
      refreshInstances();
    };

    channel.bind('feature-unlocked', handleUnlock);
    channel.bind('new-message', handleUnlock);
    channel.bind('chat-list-update', handleUnlock);

    return () => {
      channel.unbind('feature-unlocked', handleUnlock);
      channel.unbind('new-message', handleUnlock);
      channel.unbind('chat-list-update', handleUnlock);
    };
  }, [team?.id]);

  return {
    // hasInstance is mapped to isUnlocked so existing layout/sidebar/dashboard guards unlock automatically!
    hasInstance: isUnlocked,
    hasActualInstance,
    isChatbotInstalled,
    hasReceivedMessages,
    isUnlocked,
    instanceCount,
    instances: Array.isArray(instances) ? instances : team?.evolutionInstances || [],
    isLoading,
    refreshInstances,
    team,
  };
}
